import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Package, Calendar, Tag, CheckCircle2, XCircle, FileText, ArrowRight, Award, TrendingUp, Users, Zap, Link2, Database,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}
function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const includesList = [
  "Setup completo de Kommo (cuenta, configuración y usuarios)",
  "Licencia de Kommo incluida: 6 meses + 1 mes de regalo",
  "Diseño y configuración del pipeline comercial adaptado a tu negocio",
  "Configuración de campos personalizados, etiquetas y segmentación",
  "Importación y limpieza de tu base de datos de contactos",
  "Integración con tus formularios web",
  "Integración con WhatsApp (nativa de Kommo)",
  "Integración con Meta Ads",
  "Integración con Google Ads",
  "Integración con API de Conversiones",
  "Automatizaciones comerciales (bienvenida, asignación, recordatorios, seguimiento, notificaciones)",
  "Templates de emails comerciales preconfigurados",
  "Formación a tu equipo (sesión de 60-90 min)",
];

const needsList = [
  "Tu base de datos de contactos actual (Excel o CSV)",
  "Cómo es tu proceso comercial actual (etapas y responsables)",
  "Datos de tu equipo de ventas",
  "Tus formularios web activos",
  "Acceso a WhatsApp Business (si aplica)",
  "Tus materiales comerciales actuales (propuestas, catálogos)",
];

const notIncluded = [
  "Usuarios adicionales de Kommo (coste por usuario aparte)",
  "Tableros y dashboards comerciales personalizados",
  "Automatizaciones operativas (pedidos, facturación, inventario, logística)",
  "Desarrollo a medida de webhooks o integraciones con APIs externas",
  "Gestión y mantenimiento continuo del CRM tras la implementación",
  "La licencia a partir del mes 8 (renovable con nosotros a 190€/semestre)",
];

const steps = [
  { n: 1, title: "Contratas el servicio", desc: "Pago único de 900€ (o 3 plazos con Klarna)." },
  { n: 2, title: "Nos das acceso", desc: "Base de datos, proceso comercial y accesos." },
  { n: 3, title: "Implementamos Kommo", desc: "Pipeline, integraciones y automatizaciones." },
  { n: 4, title: "Formamos a tu equipo", desc: "Sesión de 60-90 min para que sepan usarlo." },
  { n: 5, title: "Listo para vender", desc: "Tu CRM funcionando desde el día uno." },
];

const benefits = [
  { icon: <Database className="w-6 h-6" />, title: "No pierdes ningún lead", desc: "Todos tus contactos organizados en un solo lugar." },
  { icon: <Zap className="w-6 h-6" />, title: "Automatizas el seguimiento", desc: "Bienvenidas, recordatorios y seguimientos automáticos." },
  { icon: <Link2 className="w-6 h-6" />, title: "Conectas tu publicidad", desc: "Los leads de Meta y Google entran directos a tu CRM." },
  { icon: <TrendingUp className="w-6 h-6" />, title: "Vendes más y mejor", desc: "Tu equipo sabe en todo momento dónde está cada cliente." },
];

const CRMImplementacionDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      <ServiceHero
        eyebrow="Servicio puntual · Pago único · Partner oficial Kommo"
        title={<>Embudo de <Highlight>Ventas</Highlight></>}
        description={<>Embudo completo con Kommo CRM: setup, pipeline, integraciones y automatizaciones. Organiza tus contactos, automatiza tu proceso comercial y no se te escapa ni un cliente.</>}
        includes={["900€ (IVA incl.) · Pago único", "Financiable en 3 plazos con Klarna", "7 meses de licencia + 1 usuario incluidos"]}
        primaryCta={{ label: "Quiero mi embudo", href: "/#contact" }}
        secondaryCta={{ label: "Ver qué incluye", href: "#incluye" }}
      />

      <div id="incluye">
        <IncludesExcludes
          includes={[
            "Setup completo de Kommo (cuenta, configuración, pipeline)",
            "<strong>Licencia de Kommo incluida: 6 meses + 1 de regalo (7 meses), con 1 usuario incluido</strong>",
            "Pipeline comercial adaptado a tu negocio",
            "Campos personalizados, etiquetas y segmentación",
            "Importación y limpieza de tu base de datos",
            "Integración con tus formularios web",
            "Integración con WhatsApp (nativa de Kommo)",
            "<strong>Integración con Meta Ads, Google Ads y API de Conversiones</strong>",
            "Automatizaciones comerciales (bienvenida, asignación, recordatorios, seguimiento, notificaciones)",
            "Templates de emails comerciales",
            "Formación a tu equipo (sesión de 60-90 min)",
          ]}
          excludes={[
            "Usuarios adicionales de Kommo (coste por usuario aparte)",
            "Tableros y dashboards comerciales personalizados",
            "Automatizaciones operativas (pedidos, facturación, inventario, logística)",
            "Desarrollo a medida de webhooks o integraciones con APIs externas",
            "Gestión y mantenimiento continuo del CRM tras la implementación",
            "La licencia a partir del mes 8 (ver renovación abajo)",
          ]}
        />
      </div>

      {/* 3 COLUMNAS */}
      <section>
        <AnimateIn>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Package className="w-6 h-6" />, title: "Qué incluye", items: ["Setup completo de Kommo", "Licencia 6 meses + 1 de regalo", "Integraciones con Meta, Google y conversiones", "Formación a tu equipo"] },
              { icon: <Calendar className="w-6 h-6" />, title: "Plazo de entrega", items: ["Entre 14 y 21 días", "Desde la recepción de accesos y base de datos"] },
              { icon: <Tag className="w-6 h-6" />, title: "Inversión", items: ["900€ (IVA incluido)", "Pago único", "Licencia incluida 7 meses"] },
            ].map((c, i) => (
              <Card key={i} className="p-6 hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">{c.icon}</div>
                <h4 className="font-semibold mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* INCLUYE */}
      <section id="incluye">
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Alcance del servicio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Todo lo que incluye tu implementación de Kommo</h3>
            <p className="text-muted-foreground">Dejamos tu CRM completamente montado, integrado y con tu equipo formado para usarlo desde el primer día.</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
          {includesList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.03}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                <span className="text-sm">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* AHORRO PARTNERS DESTACADO */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <div className="flex items-start gap-5 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Award className="w-7 h-7" />
              </div>
              <div>
                <Badge className="mb-2 bg-primary/15 text-primary border-primary/30 hover:bg-primary/20">Partner oficial Kommo</Badge>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Somos partners oficiales de Kommo — y tú te ahorras dinero</h3>
                <p className="text-muted-foreground">Al ser partners oficiales, tu implementación incluye ventajas que no tendrías por tu cuenta:</p>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-border bg-background">
              <div className="grid grid-cols-3 text-sm font-semibold bg-muted/50">
                <div className="p-4"></div>
                <div className="p-4 text-center text-muted-foreground">Por tu cuenta</div>
                <div className="p-4 text-center text-primary">Con Vibra</div>
              </div>
              <div className="grid grid-cols-3 text-sm border-t border-border">
                <div className="p-4 font-medium">Primeros 7 meses de licencia</div>
                <div className="p-4 text-center text-muted-foreground line-through">273€ (39€/mes)</div>
                <div className="p-4 text-center font-bold text-primary">Incluidos (0€)</div>
              </div>
              <div className="grid grid-cols-3 text-sm border-t border-border">
                <div className="p-4 font-medium">Renovación tras el mes 7</div>
                <div className="p-4 text-center text-muted-foreground line-through">234€/semestre (39€/mes)</div>
                <div className="p-4 text-center font-bold text-primary">190€/semestre</div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-6 leading-relaxed">
              Tu implementación incluye <strong className="text-foreground">7 meses de licencia + 1 usuario</strong> (un valor de 273€). A partir del mes 8, renovando con nosotros pagas <strong className="text-foreground">190€ cada 6 meses en lugar de 234€</strong> — un ahorro de <strong className="text-foreground">88€ al año, todos los años</strong>.
            </p>
          </div>
        </AnimateIn>
      </section>

      {/* QUÉ NECESITAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Antes de empezar</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué necesitamos para empezar</h3>
            <p className="text-muted-foreground">Para arrancar la implementación y cumplir el plazo de entrega, necesitamos que nos facilites:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-3">
          {needsList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText size={16} />
                </div>
                <span className="text-sm pt-1">{item}</span>
              </div>
            </AnimateIn>
          ))}
          <AnimateIn>
            <p className="text-sm text-muted-foreground text-center pt-4 italic">
              El plazo de entrega (14-21 días) empieza a contar cuando recibimos los accesos y la base de datos.
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* QUÉ NO INCLUYE */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="outline" className="mb-4 border-destructive/40 text-destructive">Sin sorpresas</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué no incluye este servicio</h3>
            <p className="text-muted-foreground">Para que todo quede claro desde el principio, estos puntos quedan fuera del servicio:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid gap-3">
          {notIncluded.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <XCircle size={18} className="text-destructive shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* CÓMO TRABAJAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h3>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-4">
          {steps.map((s, i) => (
            <AnimateIn key={s.n} delay={i * 0.06}>
              <div className="flex items-start gap-5 p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground flex items-center justify-center font-bold text-lg shrink-0">
                  {s.n}
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{s.title}</h4>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* BENEFICIOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Para qué te sirve</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué consigues con tu CRM</h3>
          </div>
        </AnimateIn>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((b, i) => (
            <AnimateIn key={i} delay={i * 0.06}>
              <Card className="p-6 h-full hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">{b.icon}</div>
                <h4 className="font-semibold mb-2">{b.title}</h4>
                <p className="text-sm text-muted-foreground">{b.desc}</p>
              </Card>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto text-center rounded-3xl p-10 md:p-16 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <h3 className="text-3xl md:text-5xl font-bold mb-4">¿Listo para organizar y automatizar tus ventas?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Pago único de 900€ con 7 meses de licencia incluidos. Tu CRM funcionando en 21 días.
            </p>
            <a href="/#contact" className="btn-pill bg-primary text-primary-foreground hover:shadow-glow px-8 py-4 inline-flex items-center gap-2">
              Implementar mi CRM
              <ArrowRight size={16} />
            </a>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default CRMImplementacionDemo;
